// Canonical OfferCard = the pre-redesign original, kept as the A/B baseline (DEV-4667).
// The v2.5 redesign lives in offerV25.card.tsx and renders only for the 'v2.5' bucket.
//
// The promo/action label is inlined here (instead of the shared ActionLabelCard)
// because that shared component was restyled for v2.5; inlining keeps this arm
// pixel-faithful to the original without forcing the shared component back.
import { useMutation } from "@apollo/client";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import MediaQuery from "react-responsive";
import { useNavigate } from "react-router-dom";
import { breakpoints, i18n, mediaQuery, RootState, setGlobalLoading, setEditableCoverageSelection, setOfferId, setOneTimeToggle, useAppDispatch, useAppSelector } from "src/common";
import { AdditionalCoveragesCard, Button, Divider, DropdownDownIcon, FilterCard, Flex, Loader, Text } from 'src/components';
import { AppRoutes } from "src/pages/app";
import { colors } from "src/theme";
import { CalculationResponseDto, CalculatorCoverageWithPriceDto, createCheckoutHealthOffer } from "../../common/api/apollo/supplementary";
import { Card } from "./card";
import { AdditionalCoverage, CoveragesCard } from "./coverages.card";
import { OfferHeaderCard } from "./parts/offerHeader.part";

// Pre-redesign promo-label colours (before theme.tsx offerPromoBg/Text were
// recoloured for v2.5). Pinned here so the original arm stays faithful.
const ORIGINAL_PROMO_BG = '#D1F7E3';
const ORIGINAL_PROMO_TEXT = '#2D5A43';

const CardStyle = styled(Card)({
  padding: '12px',

  [mediaQuery('mobile')]: {
    padding: '20px'
  },
})

const ButtonStyle = styled(Button)({
  width: '100%',

  [mediaQuery('mobile')]: {
    width: 'auto'
  },
})

const OfferCardRoot = styled(CardStyle)({
  position: 'relative',
})

const CalculationsLoadingOverlay = styled.div({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.55)',
  borderRadius: 'inherit',
  zIndex: 3,
})

interface OfferCardProps {
  offer: CalculationResponseDto;
  loading?: boolean;
  oneTimeToggle?: boolean;
  bListToggle?: boolean;
  className?: string;
  showSelectButton?: boolean;
  /** True while supplementary pricing query is refetching (e.g. after toggling editOnWeb coverage). */
  calculationsLoading?: boolean;
  onSupplementaryCoverageToggle?: () => void;
  isComparisonFunnel?: boolean;
}

export const OfferCard = ({ offer, loading, oneTimeToggle = false, bListToggle = false, className, showSelectButton = true, calculationsLoading = false, isComparisonFunnel = false }: OfferCardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createCheckoutHealthOfferMutation] = useMutation(createCheckoutHealthOffer);
  const soIdWeb = useAppSelector((store: RootState) => store.model.offer.soIdWeb);
  const editableCoverageSelections = useAppSelector((store: RootState) => store.model.offer.editableCoverageSelections);

  const [isMoreDetailsOpen, setIsMoreDetailsOpen] = useState(false);
  const [isYearToggleChecked, setIsYearToggleChecked] = useState(oneTimeToggle);

  useEffect(() => {
    setIsYearToggleChecked(oneTimeToggle);
  }, [oneTimeToggle]);

  // editOnWeb coverages (e.g. eCard / "Zeleni popust") render as toggles below,
  // so we exclude them from "Moguća dodatna pokrića" to avoid duplication.
  const nonEditableAdditionalCoverages = (offer?.coverages ?? []).filter(
    (c) => c?.editOnWeb !== true
  );

  const editableCoverages = (offer?.coverages ?? []).filter(
    (c) => c.editOnWeb && (!c.type || c.type === 'checkbox')
  );

  useEffect(() => {
    if (!offer?.code || editableCoverages.length === 0) return;
    editableCoverages.forEach((cov: { code: string }) => {
      dispatch(setEditableCoverageSelection({ code: cov.code, value: editableCoverageSelections?.[cov.code] ?? false }));
    });
  }, [offer?.code, editableCoverages.length]);

  if (loading) {
    return (
      <Card radius={12} padding={20} className={className}>
        <Loader />
      </Card>
    )
  }

  const discountPercentage = ((offer.prices.generalTransferWithInstallments - offer.prices.generalTransfer) / offer.prices.generalTransferWithInstallments) * 100;
  const roundedDiscountPercentage = Math.round(discountPercentage); // Zaokruživanje na više bez decimalnih mjesta

  const selectOffer = () => {
    const selectedFromCard = editableCoverages
      .filter((c: { code: string }) => editableCoverageSelections?.[c.code] === true)
      .map((c: { code: string }) => c.code);
    const withPageLevel = editableCoverageSelections?.eCard ? [...selectedFromCard, 'eCard'] : selectedFromCard;
    const selectedCodes = Array.from(new Set(withPageLevel));
    dispatch(setGlobalLoading(true));
    createCheckoutHealthOfferMutation({
      variables: {
        id: soIdWeb,
        input: {
          productCode: offer.code,
          coverages: selectedCodes
        }
      }
    })
      .then((response) => {
        const offerId = response.data.createHealthOffer;
        dispatch(setOfferId(offerId));
        dispatch(setOneTimeToggle(isYearToggleChecked));
        navigate(`${AppRoutes.OFFER.fullPath}/${offerId}`);
      })
      .catch((error) => {
        console.error('Greška prilikom izvršavanja mutacije:', error);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  return (
    <OfferCardRoot radius={12} padding={20} className={className}>
      <Flex vertical gap={12}>
        <OfferHeaderCard
          brandName={offer.brandName!}
          offerLabel={offer.label!}
          price={isYearToggleChecked ? offer.prices.generalTransfer : (offer.prices.generalTransferWithInstallments / 12)}
          priceType={isYearToggleChecked ? 'yearly' : 'monthly'}
          logoUrl={offer.logoUrl!}
          discountPercentage={roundedDiscountPercentage}
          handlePriceTypeChange={() => setIsYearToggleChecked(!isYearToggleChecked)}
          isComparisonFunnel={isComparisonFunnel}
          diffPrice={offer.diffPrice ?? undefined}
        />
        <Divider dashed />
        {offer.actionLabel ? (
          <Card radius={10} padding="12px 16px" background={ORIGINAL_PROMO_BG}>
            <Text type="small" color={ORIGINAL_PROMO_TEXT}>{offer.actionLabel}</Text>
          </Card>
        ) : null}
        <MediaQuery maxWidth={breakpoints.mobile - 1}>
          <CoveragesCard coverages={offer.fixedCoverages || []} isExpanded={false} bListExpand={bListToggle} expandable title={offer.label} />
        </MediaQuery>
        <MediaQuery minWidth={breakpoints.mobile}>
          <CoveragesCard coverages={offer.fixedCoverages || []} additionalCoverages={nonEditableAdditionalCoverages as AdditionalCoverage[]} isExpanded={isMoreDetailsOpen} bListExpand={bListToggle} callMeBtn />
        </MediaQuery>
        {editableCoverages.length > 0 && (
          <>
            {editableCoverages.map((cov: CalculatorCoverageWithPriceDto) => (
              <FilterCard
                key={cov.code}
                title={cov.label ?? cov.code}
                description={cov.description || ''}
                checked={editableCoverageSelections?.[cov.code] !== false}
                onToggleChange={(checked) =>
                  dispatch(setEditableCoverageSelection({ code: cov.code, value: checked }))
                }
              />
            ))}
          </>
        )}

        <Flex justify='space-between' gap={8}>
          <MediaQuery minWidth={breakpoints.mobile}>
            <Button
              btnType="textPrimary"
              iconPosition="right"
              onClick={() => { setIsMoreDetailsOpen(!isMoreDetailsOpen) }}
              text={isMoreDetailsOpen ? i18n.t('pages.offersPage.hideDetails') : i18n.t('pages.offersPage.showDetails')}
            >
              <DropdownDownIcon color={colors.icon4} isOpen={isMoreDetailsOpen} />
            </Button>
          </MediaQuery>
          {showSelectButton && <ButtonStyle
            btnType="primary"
            onClick={() => selectOffer()}
            text="Kupi"
          />}
        </Flex>
        {nonEditableAdditionalCoverages && nonEditableAdditionalCoverages.length > 0 && <MediaQuery maxWidth={breakpoints.mobile - 1}>
          <Divider dashed />
          <AdditionalCoveragesCard additionalCoverages={nonEditableAdditionalCoverages} />
        </MediaQuery>}
      </Flex>
      {calculationsLoading && (
        <CalculationsLoadingOverlay aria-busy="true" aria-live="polite">
          <Loader size={32} />
        </CalculationsLoadingOverlay>
      )}
    </OfferCardRoot>
  )
};
